import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LogoFull } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { LogOut, Loader2, Users, Inbox, CheckCircle2, X, Download } from "lucide-react";
import { toast } from "sonner";
import { businessLines } from "@/data/businessLines";
import { profileOptions } from "@/data/content";

const STATUSES = ["nuovo", "contattato", "chiuso"];
const TOOLTIP_STYLE = { background: "#0D1B2A", border: "1px solid #C5A059", borderRadius: 0 };
const statusColor = {
  nuovo: "text-[#C5A059] border-[#C5A059]/40",
  contattato: "text-blue-300 border-blue-400/40",
  chiuso: "text-gray-400 border-white/20",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fProfile, setFProfile] = useState("all");
  const [fLine, setFLine] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    const params = {};
    if (fProfile !== "all") params.profile = fProfile;
    if (fLine !== "all") params.business_line = fLine;
    if (fStatus !== "all") params.status = fStatus;
    const [l, s] = await Promise.all([
      api.get("/leads", { params }),
      api.get("/leads/stats"),
    ]);
    setLeads(l.data);
    setStats(s.data);
    setLoading(false);
  }, [fProfile, fLine, fStatus]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const updateLead = async (id, patch) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, patch);
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      toast.success("Lead aggiornato");
      api.get("/leads/stats").then((r) => setStats(r.data));
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const exportCsv = () => {
    if (!leads.length) {
      toast.error("Nessun lead da esportare");
      return;
    }
    const cols = [
      ["created_at", "Data"],
      ["first_name", "Nome"],
      ["last_name", "Cognome"],
      ["email", "Email"],
      ["phone", "Telefono"],
      ["profile", "Profilo"],
      ["business_line", "Linea"],
      ["source_page", "Provenienza"],
      ["capital", "Capitale"],
      ["horizon", "Orizzonte"],
      ["status", "Stato"],
      ["internal_note", "Nota interna"],
      ["message", "Messaggio"],
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = cols.map(([, label]) => esc(label)).join(",");
    const rows = leads.map((l) => cols.map(([key]) => esc(l[key])).join(","));
    const csv = "\uFEFF" + [header, ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `opera-lead-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${leads.length} lead esportati`);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#C5A059]" size={28} />
      </div>
    );
  }

  const monthData = stats?.by_month || [];
  const lineData = Object.entries(stats?.by_line || {}).map(([name, count]) => ({ name, count }));

  return (
    <div className="min-h-screen bg-[#0D1B2A]">
      {/* HEADER */}
      <header className="bg-[#091623] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <LogoFull compact />
          <div className="flex items-center gap-4">
            <a
              href="/back-office"
              data-testid="dash-backoffice-link"
              className="hidden sm:block text-sm text-[#C5A059] hover:underline"
            >
              Back Office Google
            </a>
            <span className="hidden sm:block text-sm text-gray-400">{user.name}</span>
            <Button
              data-testid="dash-logout"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              variant="ghost"
              className="text-gray-300 hover:text-[#C5A059] rounded-none"
            >
              <LogOut size={16} className="mr-2" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="label-eyebrow">Dashboard riservata</span>
            <h1 className="font-display font-light text-3xl text-white mt-2">Lead & pipeline</h1>
          </div>
          <Button
            data-testid="dash-export-csv"
            onClick={exportCsv}
            className="bg-transparent border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10 rounded-none font-medium"
          >
            <Download size={16} className="mr-2" /> Esporta CSV
          </Button>
        </div>

        {/* STAT CARDS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {[
            { key: "total", icon: Inbox, label: "Lead totali", value: stats?.total ?? "—" },
            { key: "nuovo", icon: Users, label: "Nuovi", value: stats?.by_status?.nuovo ?? 0 },
            { key: "contattato", icon: CheckCircle2, label: "Contattati", value: stats?.by_status?.contattato ?? 0 },
            { key: "chiuso", icon: CheckCircle2, label: "Chiusi", value: stats?.by_status?.chiuso ?? 0 },
          ].map((c) => (
            <div key={c.key} data-testid={`stat-${c.key}`} className="bg-[#1E2229] border border-white/5 p-5">
              <c.icon className="text-[#C5A059]" size={20} strokeWidth={1.5} />
              <div className="font-display text-3xl text-white mt-3 tabular-nums">{c.value}</div>
              <div className="text-xs text-gray-500 mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        {/* CHARTS */}
        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          <div className="bg-[#1E2229] border border-white/5 p-5">
            <div className="label-eyebrow mb-4">Lead per mese</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="month" stroke="#8a8f98" fontSize={11} />
                <YAxis stroke="#8a8f98" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" fill="#C5A059" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-[#1E2229] border border-white/5 p-5">
            <div className="label-eyebrow mb-4">Lead per linea di business</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={lineData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" horizontal={false} />
                <XAxis type="number" stroke="#8a8f98" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#8a8f98" fontSize={9} width={110} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" fill="#C5A059" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* FILTERS */}
        <div className="flex flex-wrap gap-3 mt-8">
          <FilterSelect testid="filter-profile" value={fProfile} onChange={setFProfile} placeholder="Profilo" options={profileOptions} />
          <FilterSelect testid="filter-line" value={fLine} onChange={setFLine} placeholder="Linea" options={businessLines.map((b) => b.name)} />
          <FilterSelect testid="filter-status" value={fStatus} onChange={setFStatus} placeholder="Stato" options={STATUSES} />
        </div>

        {/* TABLE */}
        <div className="mt-4 bg-[#1E2229] border border-white/5 overflow-x-auto">
          {loading ? (
            <div className="p-10 flex justify-center">
              <Loader2 className="animate-spin text-[#C5A059]" size={24} />
            </div>
          ) : leads.length === 0 ? (
            <div className="p-10 text-center text-gray-500">Nessun lead corrispondente ai filtri.</div>
          ) : (
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="text-left text-gray-500 border-b border-white/10">
                  <th className="p-3 font-medium">Data</th>
                  <th className="p-3 font-medium">Nome</th>
                  <th className="p-3 font-medium">Profilo</th>
                  <th className="p-3 font-medium">Linea</th>
                  <th className="p-3 font-medium">Contatti</th>
                  <th className="p-3 font-medium">Stato</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    data-testid={`lead-row-${l.id}`}
                    onClick={() => {
                      setSelected(l);
                      setNote(l.internal_note || "");
                    }}
                    className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                  >
                    <td className="p-3 text-gray-400 whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</td>
                    <td className="p-3 text-white">{l.first_name} {l.last_name}</td>
                    <td className="p-3 text-gray-300">{l.profile || "—"}</td>
                    <td className="p-3 text-gray-300">{l.business_line || "—"}</td>
                    <td className="p-3 text-gray-400">{l.email}</td>
                    <td className="p-3">
                      <span className={`text-xs border px-2 py-1 uppercase tracking-wide ${statusColor[l.status] || ""}`}>
                        {l.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* DETAIL DRAWER */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-[#091623] border-l border-white/10 h-full overflow-y-auto p-6">
            <button className="absolute top-5 right-5 text-gray-400 hover:text-white" onClick={() => setSelected(null)} data-testid="drawer-close">
              <X size={20} />
            </button>
            <span className="label-eyebrow">Dettaglio lead</span>
            <h2 className="font-display text-2xl text-white mt-2">{selected.first_name} {selected.last_name}</h2>

            <dl className="mt-6 space-y-3 text-sm">
              {[
                ["Email", selected.email],
                ["Telefono", selected.phone],
                ["Profilo", selected.profile],
                ["Linea", selected.business_line],
                ["Provenienza", selected.source_page],
                ["Capitale", selected.capital],
                ["Orizzonte", selected.horizon],
                ["Data", (selected.created_at || "").replace("T", " ").slice(0, 16)],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-3">
                  <dt className="text-gray-500 w-24 shrink-0">{k}</dt>
                  <dd className="text-gray-200">{v || "—"}</dd>
                </div>
              ))}
            </dl>

            {selected.message && (
              <div className="mt-5">
                <div className="text-gray-500 text-sm mb-1">Messaggio</div>
                <p className="text-gray-200 text-sm bg-[#1E2229] p-3 leading-relaxed">{selected.message}</p>
              </div>
            )}

            <div className="mt-6">
              <div className="label-eyebrow mb-2">Stato</div>
              <div className="flex gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`set-status-${s}`}
                    onClick={() => updateLead(selected.id, { status: s })}
                    className={`text-xs border px-3 py-2 uppercase tracking-wide transition-colors ${
                      selected.status === s ? "bg-[#C5A059] text-[#0D1B2A] border-[#C5A059]" : "text-gray-300 border-white/15 hover:border-[#C5A059]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <div className="label-eyebrow mb-2">Nota interna</div>
              <Textarea
                data-testid="drawer-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="bg-[#1E2229] border-white/10 text-white rounded-none min-h-[100px]"
              />
              <Button
                data-testid="drawer-save-note"
                onClick={() => updateLead(selected.id, { internal_note: note })}
                className="mt-3 bg-[#C5A059] text-[#0D1B2A] hover:bg-[#D8B978] rounded-none"
              >
                Salva nota
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-[200px] bg-[#1E2229] border-white/10 text-white rounded-none h-10">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-[#1E2229] border-white/10 text-white">
        <SelectItem value="all">Tutti — {placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
